import { useRef, useState } from "react";
import {
  Check,
  Download,
  Loader2,
  Paperclip,
  UploadCloud,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

// Sub-bucket 4 — hoisted file-attachment UI. Replaces the forked
// per-feature variants that grew up under sub-bucket 2 (forms file-field —
// single-file dropzone) and sub-bucket 3 (mdf-attachment-field — multi-file
// list). Journey task attachments are the rule-of-three trigger.
//
// Visual contract:
//   • `multiple={false}` → dropzone + 4-state (empty / uploading / uploaded
//     / error). Matches sub-bucket 2's forms file-field.
//   • `multiple={true}`  → list of uploaded rows + "Add file" button.
//     Matches sub-bucket 3's MDF multi-file behavior.
//
// Caller contract: upload + remove + signed-url-download are passed as
// async callbacks. The component delegates network work to the caller's
// React Query hooks so each feature retains its own invalidation +
// optimistic patches against feature-specific query keys.
//
// File validation (size + MIME) happens IN-COMPONENT before invoking
// `onUpload`, so the visual error state lives next to the upload affordance.

export type FileAttachmentRow = {
  id: string;
  file_name: string;
  size_bytes: number;
  content_type?: string | null;
};

type Props = {
  /** Rows already persisted server-side; component renders these as the "uploaded" UI. */
  attachments: FileAttachmentRow[];
  /** Caller's upload mutation. Should return the persisted row (or at minimum `id + file_name + size_bytes`). */
  onUpload: (file: File) => Promise<FileAttachmentRow>;
  /** Caller's remove mutation. Optional — if absent, remove affordance is hidden. */
  onRemove?: (id: string) => Promise<void>;
  /**
   * Caller's signed-url fetcher. Optional — if provided, uploaded rows
   * render as download links instead of a file icon. The returned URL is
   * usually short-lived (e.g. 1h) and minted on click.
   */
  getDownloadUrl?: (id: string) => Promise<string>;

  multiple?: boolean;
  maxFiles?: number;
  maxSizeBytes?: number;
  acceptedMimeTypes?: readonly string[];
  disabled?: boolean;

  /** Label rendered above the field. Defaults vary by mode. Pass `null` to suppress (e.g. when an outer FieldShell renders its own label). */
  label?: string | null;
  /** Helper copy under the label. Defaults to a generated "Up to N MB · types" line. Pass `null` to suppress. */
  helpText?: string | null;
  className?: string;
};

type LocalState =
  | { kind: "empty" }
  | { kind: "uploading"; file: File }
  | { kind: "error"; file: File; message: string };

export function FileAttachmentField({
  attachments,
  onUpload,
  onRemove,
  getDownloadUrl,
  multiple = false,
  maxFiles = multiple ? 10 : 1,
  maxSizeBytes = 25 * 1024 * 1024,
  acceptedMimeTypes,
  disabled,
  label,
  helpText,
  className,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [local, setLocal] = useState<LocalState>({ kind: "empty" });
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const accept = acceptedMimeTypes?.join(",");
  const atCap = attachments.length >= maxFiles;

  function validateFile(file: File): string | null {
    if (file.size > maxSizeBytes) {
      return `${file.name} is larger than ${formatBytes(maxSizeBytes)}.`;
    }
    if (
      acceptedMimeTypes &&
      !(acceptedMimeTypes as readonly string[]).includes(file.type)
    ) {
      return `${file.name} is not a supported file type.`;
    }
    return null;
  }

  async function handleFile(file: File) {
    const err = validateFile(file);
    if (err) {
      setLocal({ kind: "error", file, message: err });
      return;
    }
    setLocal({ kind: "uploading", file });
    try {
      await onUpload(file);
      setLocal({ kind: "empty" });
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : "Upload failed. Try again.";
      setLocal({ kind: "error", file, message });
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function handleMultiPick(files: FileList | null) {
    if (!files || files.length === 0) return;
    const remaining = maxFiles - attachments.length;
    if (files.length > remaining) {
      toast.error(
        `You can attach at most ${maxFiles} file${maxFiles === 1 ? "" : "s"} (${remaining} slot${remaining === 1 ? "" : "s"} left).`,
      );
      return;
    }
    for (const file of Array.from(files)) {
      const err = validateFile(file);
      if (err) {
        toast.error(err);
        continue;
      }
      setLocal({ kind: "uploading", file });
      try {
        await onUpload(file);
      } catch (caught) {
        const message =
          caught instanceof Error ? caught.message : "Upload failed. Try again.";
        toast.error(message);
        break;
      } finally {
        setLocal({ kind: "empty" });
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  }

  async function handleRemove(id: string) {
    if (!onRemove) return;
    setRemovingId(id);
    try {
      await onRemove(id);
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : "Failed to remove.";
      toast.error(message);
    } finally {
      setRemovingId(null);
    }
  }

  async function handleDownload(id: string) {
    if (!getDownloadUrl) return;
    setDownloadingId(id);
    try {
      const url = await getDownloadUrl(id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : "Failed to fetch download URL.";
      toast.error(message);
    } finally {
      setDownloadingId(null);
    }
  }

  const showLabel = label !== null;
  const effectiveLabel = showLabel
    ? (label ?? (multiple ? "Attachments" : "Attachment"))
    : null;
  const showHelp = helpText !== null;
  const effectiveHelp = showHelp
    ? (helpText ??
      (multiple
        ? `Attach up to ${maxFiles} files (max ${formatBytes(maxSizeBytes)} each).`
        : `Up to ${formatBytes(maxSizeBytes)} per file.`))
    : null;

  // ---- Single-file mode (dropzone + 4-state) ----
  if (!multiple) {
    const uploaded = attachments[0] ?? null;
    return (
      <div className={className}>
        {effectiveLabel !== null ? (
          <label className="block text-sm font-medium text-foreground">
            {effectiveLabel}
          </label>
        ) : null}
        {effectiveHelp ? (
          <p className="mt-1 text-xs text-muted-foreground">{effectiveHelp}</p>
        ) : null}

        <div className="mt-2">
          {uploaded ? (
            <SingleUploadedRow
              row={uploaded}
              onRemove={onRemove ? () => handleRemove(uploaded.id) : undefined}
              onDownload={
                getDownloadUrl ? () => handleDownload(uploaded.id) : undefined
              }
              isRemoving={removingId === uploaded.id}
              isDownloading={downloadingId === uploaded.id}
              disabled={disabled}
            />
          ) : local.kind === "uploading" ? (
            <UploadingRow file={local.file} />
          ) : local.kind === "error" ? (
            <ErrorRow
              file={local.file}
              message={local.message}
              onReset={() => setLocal({ kind: "empty" })}
            />
          ) : (
            <Dropzone
              accept={accept}
              inputRef={inputRef}
              disabled={disabled}
              onFile={handleFile}
            />
          )}
        </div>
      </div>
    );
  }

  // ---- Multi-file mode (list + "Add file" button) ----
  return (
    <div className={className}>
      {effectiveLabel !== null ? (
        <label className="block text-sm font-medium text-foreground">
          {effectiveLabel}
        </label>
      ) : null}
      {effectiveHelp ? (
        <p className="mt-1 text-xs text-muted-foreground">{effectiveHelp}</p>
      ) : null}

      {attachments.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between rounded-md border bg-card px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm">{a.file_name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatBytes(a.size_bytes)}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {getDownloadUrl ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={disabled || downloadingId === a.id}
                    onClick={() => handleDownload(a.id)}
                    aria-label={`Download ${a.file_name}`}
                    className="h-7 w-7 p-0"
                  >
                    {downloadingId === a.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                  </Button>
                ) : null}
                {onRemove ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={disabled || removingId === a.id}
                    onClick={() => handleRemove(a.id)}
                    aria-label={`Remove ${a.file_name}`}
                    className="h-7 w-7 p-0"
                  >
                    {removingId === a.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <X className="h-3.5 w-3.5" />
                    )}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-3">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          className="sr-only"
          onChange={(e) => handleMultiPick(e.target.files)}
          disabled={disabled || atCap}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || atCap || local.kind === "uploading"}
          onClick={() => inputRef.current?.click()}
          className={cn(
            "inline-flex items-center gap-2",
            atCap ? "opacity-60" : null,
          )}
        >
          {local.kind === "uploading" ? (
            <>
              <UploadCloud className="h-4 w-4 animate-pulse" />
              Uploading {local.file.name}
            </>
          ) : (
            <>
              <UploadCloud className="h-4 w-4" />
              {attachments.length === 0 ? "Add file" : "Add another"}
            </>
          )}
        </Button>
        {atCap ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Limit reached. Remove an existing file to add another.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal helper components.
// ---------------------------------------------------------------------------

function Dropzone({
  accept,
  inputRef,
  disabled,
  onFile,
}: {
  accept: string | undefined;
  inputRef: React.RefObject<HTMLInputElement | null>;
  disabled?: boolean;
  onFile: (file: File) => void;
}) {
  return (
    <div
      className="flex flex-col items-center gap-2 rounded-md border border-dashed bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) onFile(file);
      }}
    >
      <UploadCloud className="h-5 w-5 text-muted-foreground" />
      <div>
        <span>Drop a file here, or </span>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          choose one
        </Button>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFile(file);
            e.target.value = "";
          }}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function UploadingRow({ file }: { file: File }) {
  return (
    <div className="space-y-2 rounded-md border bg-card px-3 py-3 text-sm">
      <div className="flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
        <span className="flex-1 truncate font-medium text-foreground">
          {file.name}
        </span>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {formatBytes(file.size)}
        </span>
      </div>
      <Progress value={null as unknown as number} className="h-1" />
    </div>
  );
}

function ErrorRow({
  file,
  message,
  onReset,
}: {
  file: File;
  message: string;
  onReset: () => void;
}) {
  return (
    <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-3 text-sm">
      <div className="flex items-center gap-2">
        <Paperclip className="h-3.5 w-3.5 shrink-0 text-destructive" />
        <span className="flex-1 truncate font-medium text-destructive">
          {file.name}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onReset}
          className="h-7 w-7 p-0"
          aria-label="Reset"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <p className="text-xs text-destructive">{message}</p>
    </div>
  );
}

function SingleUploadedRow({
  row,
  onRemove,
  onDownload,
  isRemoving,
  isDownloading,
  disabled,
}: {
  row: FileAttachmentRow;
  onRemove?: () => void;
  onDownload?: () => void;
  isRemoving?: boolean;
  isDownloading?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm">
      <Check className="h-4 w-4 shrink-0 text-status-active" />
      <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate font-medium text-foreground">
        {row.file_name}
      </span>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {formatBytes(row.size_bytes)}
      </span>
      {onDownload ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onDownload}
          disabled={disabled || isDownloading}
          className="h-7 w-7 p-0"
          aria-label="Download"
        >
          {isDownloading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
        </Button>
      ) : null}
      {onRemove ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onRemove}
          disabled={disabled || isRemoving}
          className="h-7 w-7 p-0"
          aria-label="Remove file"
        >
          {isRemoving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <X className="h-3.5 w-3.5" />
          )}
        </Button>
      ) : null}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
